import React, { useState } from 'react';
import Icon from '../ui/Icon';
import './CognitiveSnapshot.css';

// Exactly 3 signals, deliberately simplified from the full proprietary
// diagnostic vocabulary (TPSI/CER/RCI/FFI): Focus reads FFI, Speed reads
// TPSI directly. Accuracy is NOT CER (a composite that also weighs
// difficulty/confidence) — it's the same plain overallStats.avg_score
// already shown elsewhere, chosen specifically so "Accuracy" never claims
// more precision than a real, simple, already-displayed percentage.
// Gated entirely behind readiness.available/confidence: TPSI/FFI can return
// a bare "50" both when performance is genuinely neutral AND when there
// isn't enough evidence to measure at all (the underlying engine collapses
// that distinction before it reaches this API) — showing a confident label
// in the second case would misrepresent a "can't tell yet" as "average."
const BANDS = [
  { min: 65, label: 'Strong', tone: 'success' },
  { min: 40, label: 'Developing', tone: 'info' },
  { min: 0, label: 'Needs attention', tone: 'warning' },
];
const bandFor = (score) => BANDS.find(b => score >= b.min);

const SIGNALS = [
  { key: 'ffi', label: 'Focus', icon: 'eye', why: 'How consistently you stay accurate as a session goes on.' },
  { key: 'tpsi', label: 'Speed', icon: 'bolt', why: 'How stable your accuracy stays under time pressure.' },
  { key: 'accuracy', label: 'Accuracy', icon: 'skill', why: 'Your average score across official assessments.' },
];

const CognitiveSnapshot = ({ readiness, avgScore }) => {
  const [openWhy, setOpenWhy] = useState(null);

  if (!readiness?.available || readiness.confidence === 'insufficient' || !readiness.sub_scores) {
    return (
      <div className="z-card cog-empty">
        Not enough evidence yet to show a cognitive snapshot — take an Aptitude Test to unlock it.
      </div>
    );
  }

  const values = {
    ffi: readiness.sub_scores.ffi != null ? Number(readiness.sub_scores.ffi) : null,
    tpsi: readiness.sub_scores.tpsi != null ? Number(readiness.sub_scores.tpsi) : null,
    accuracy: avgScore != null ? Number(avgScore) : null,
  };

  return (
    <div className="cog-grid">
      {SIGNALS.map(s => {
        const score = values[s.key];
        if (score == null) return null;
        const band = bandFor(score);
        return (
          <div key={s.key} className="cog-card">
            <div className="cog-card-top">
              <span className="cog-icon" aria-hidden="true"><Icon name={s.icon} size={18} /></span>
              <span className="cog-label">{s.label}</span>
            </div>
            <span className={`cog-value cog-value--${band.tone}`}>{band.label}</span>
            <button type="button" className="cog-why-btn" onClick={() => setOpenWhy(openWhy === s.key ? null : s.key)}>
              {openWhy === s.key ? 'Hide' : 'Why?'}
            </button>
            {openWhy === s.key && <p className="cog-why-text">{s.why} This is a skill you can improve with practice.</p>}
          </div>
        );
      })}
    </div>
  );
};

export default CognitiveSnapshot;
